import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { WALL_PRINTING_SERVICE, wallPrintingServiceForEntry } from "@/data/wallPrinting";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CheckCircle, X, MessageSquare } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { trackConversion } from "@/lib/track";
import { CONTACT_SMS } from "@/data/serviceSms";

export const OFFICE_CONSULTATION_SERVICE = "Office Color & Finish Consultation";
const SERVICE_OPTIONS = ["Venetian & Decorative Plaster", "Limewash", "Microcement", "Plaster", "Plaster Fireplace", "Plaster Kitchen Hood", "Painting", "Interior Painting", "New Construction Painting", "Commercial Painting", "Exterior Painting", "Both", "Cabinet Painting", "Wood Staining & Refinishing", "Deck Staining", "Not Sure"] as const;
const QUOTE_SERVICE_OPTIONS = [...SERVICE_OPTIONS.slice(0, 6), WALL_PRINTING_SERVICE, ...SERVICE_OPTIONS.slice(6)];
const CONTACT_SERVICE_OPTIONS = [...SERVICE_OPTIONS.slice(0, 6), WALL_PRINTING_SERVICE, OFFICE_CONSULTATION_SERVICE, ...SERVICE_OPTIONS.slice(6)];
const CONTACT_METHODS = ["Text", "Call", "Email"] as const;
const TIMING_OPTIONS = [
  "As soon as possible",
  "Within 1 month",
  "Within 1–3 months",
  "Within 3–6 months",
  "Planning for the future",
] as const;

const MAX_PHOTOS = 6;
const MAX_PHOTO_BYTES = 10 * 1024 * 1024;
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/heic"];

const mapProjectTypeToService = (projectType?: string) => {
  if (!projectType) return "";
  if (projectType === WALL_PRINTING_SERVICE) return WALL_PRINTING_SERVICE;
  const lower = projectType.toLowerCase();
  if (lower.includes("plaster") || lower.includes("limewash") || lower.includes("microcement") || lower.includes("roman clay") || lower.includes("tadelakt") || lower.includes("lime")) {
    return "Plaster";
  }
  if (lower.includes("painting") || lower.includes("staining") || lower.includes("cabinet") || lower.includes("commercial")) {
    return "Painting";
  }
  if (lower.includes("not sure") || lower.includes("not sure yet")) {
    return "Not Sure";
  }
  return "";
};

interface QuoteFormProps {
  sourcePage?: string;
  defaultProjectType?: string;
  /** "contact" = Contact page layout (required phone + town, extra service option) */
  variant?: "default" | "contact";
  defaultService?: string;
}

const QuoteForm = ({ sourcePage, defaultProjectType, variant = "default", defaultService }: QuoteFormProps) => {
  const { pathname, search } = useLocation();
  const isContact = variant === "contact";
  const serviceOptions: readonly string[] = isContact ? CONTACT_SERVICE_OPTIONS : QUOTE_SERVICE_OPTIONS;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [photos, setPhotos] = useState<File[]>([]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    service: defaultService || wallPrintingServiceForEntry(pathname, search, sourcePage) || mapProjectTypeToService(defaultProjectType) || "",
    contactMethod: "",
    timing: "",
    details: "",
  });

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!form.name.trim()) newErrors.name = "Please enter your name.";

    const phone = form.phone.trim();
    const email = form.email.trim();

    if (isContact) {
      if (!phone) newErrors.phone = "Please enter your phone number.";
      if (!form.address.trim()) newErrors.address = "Please enter your project address or town.";
    } else if (!phone && !email) {
      newErrors.contact =
        "Please provide either a phone number or an email address so we can contact you.";
    }
    if (phone && phone.replace(/\D/g, "").length < 10) {
      newErrors.phone = "Please enter a valid 10-digit phone number.";
    }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Please enter a valid email address.";
    }
    if (!form.service) newErrors.service = "Please select a service.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    const valid: string[] = [];
    const accepted = selected.filter((file) => {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        valid.push(`${file.name} isn't a supported image type.`);
        return false;
      }
      if (file.size > MAX_PHOTO_BYTES) {
        valid.push(`${file.name} is larger than 10 MB.`);
        return false;
      }
      return true;
    });
    setPhotos((prev) => [...prev, ...accepted].slice(0, MAX_PHOTOS));
    setErrors((prev) => ({ ...prev, photos: valid[0] ?? "" }));
    e.target.value = "";
  };

  const removePhoto = (index: number) => {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadPhotos = async () => {
    if (photos.length === 0) return [] as string[];
    const folder = `quotes/${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const paths: string[] = [];
    for (const file of photos) {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_").slice(-60);
      const path = `${folder}/${safeName}`;
      const { error } = await supabase.storage.from("quote-photos").upload(path, file, {
        contentType: file.type,
        upsert: false,
      });
      if (error) throw new Error("Photo upload failed");
      paths.push(path);
    }
    return paths;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const photoPaths = await uploadPhotos();

      const summary = [
        `Service: ${form.service}`,
        form.address.trim() ? `Project address or town: ${form.address.trim()}` : null,
        form.contactMethod ? `Preferred contact method: ${form.contactMethod}` : null,
        form.timing ? `Desired project timing: ${form.timing}` : null,
        form.details.trim() ? `Details: ${form.details.trim()}` : null,
        photoPaths.length
          ? `Photos uploaded (${photoPaths.length}): ${photoPaths.join(", ")}`
          : null,
      ]
        .filter(Boolean)
        .join("\n");

      const { error: saveError } = await supabase.from("leads").insert({
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        message: summary,
        source_page: sourcePage || window.location.pathname,
      });

      if (saveError) throw saveError;

      if (form.email.trim()) {
        await supabase.functions.invoke("send-estimate-request", {
          body: {
            name: form.name.trim(),
            email: form.email.trim(),
            phone: form.phone.trim(),
            message: `New Quote Request\n\n${summary}\n\nSource: ${sourcePage || window.location.pathname}`,
          },
        });
      }

      if (photoPaths.length) {
        trackConversion("photo_upload", { form: variant, count: photoPaths.length });
      }
      trackConversion("lead_form_submit", { form: variant, service: form.service });
      setSubmitted(true);
    } catch (error) {
      setErrors({ submit: "Something went wrong. Please try again or call us at (201) 701-8248." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateField = (field: keyof typeof form, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      if (field === "phone" || field === "email") delete next.contact;
      return next;
    });
  };

  if (submitted && isContact) {
    return (
      <div className="border border-border bg-card p-8 md:p-12 text-center shadow-soft" role="status">
        <CheckCircle className="w-12 h-12 text-clay mx-auto mb-5" strokeWidth={1.25} />
        <h3 className="font-heading text-2xl md:text-3xl text-foreground mb-4">Request received</h3>
        <p className="text-muted-foreground max-w-md mx-auto leading-relaxed">
          Thank you—your request has been received. Our team will review your details and contact you to discuss your project. You can also
          text additional project photos to (201) 701-8248.
        </p>
        <a
          href={CONTACT_SMS}
          onClick={() => trackConversion("text_click", { location: "form_confirmation" })}
          className="mt-8 inline-flex items-center justify-center gap-2 min-h-[48px] bg-clay hover:bg-clay-dark text-primary-foreground px-8 py-4 text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors"
        >
          <MessageSquare className="w-4 h-4" aria-hidden="true" />
          Text Additional Photos
        </a>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="border border-border bg-card p-10 md:p-12 text-center shadow-soft">
        <CheckCircle className="w-12 h-12 text-clay mx-auto mb-5" />
        <h3 className="font-heading text-2xl md:text-3xl text-foreground mb-4">Thank you!</h3>
        <p className="text-muted-foreground max-w-md mx-auto leading-relaxed">
          Thank you for contacting All Time Paint &amp; Plaster Co. Tambi or a member of our
          estimating team will contact you to discuss your project and
          schedule a walkthrough.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/"
            className="inline-flex items-center justify-center bg-clay hover:bg-clay-dark text-primary-foreground px-8 py-4 text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors"
          >
            Return to Homepage
          </Link>
          <Link
            to="/projects/"
            className="inline-flex items-center justify-center border border-foreground/25 hover:border-foreground text-foreground px-8 py-4 text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors"
          >
            View Recent Projects
          </Link>
        </div>
      </div>
    );
  }

  return (
    <form aria-busy={isSubmitting} onSubmit={handleSubmit} className="border border-border bg-card p-6 sm:p-7 md:p-10 shadow-soft space-y-6">
      {isContact && (
        <p className="text-xs text-muted-foreground">
          We’ll use your information only to respond to your project request.
        </p>
      )}
      <div className="grid gap-5 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="q-name">{isContact ? "First Name *" : "Name *"}</Label>
          <Input
            id="q-name"
            value={form.name}
            onChange={(e) => updateField("name", e.target.value)}
            maxLength={80}
            className="h-12"
            placeholder="Your name"
            autoComplete={isContact ? "given-name" : "name"}
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? "q-name-error" : undefined}
          />
          {errors.name && <p id="q-name-error" className="text-xs text-destructive">{errors.name}</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="q-phone">
            {isContact ? "Phone Number *" : <>Phone <span className="text-muted-foreground text-xs">(phone or email)</span></>}
          </Label>
          <Input
            id="q-phone"
            type="tel"
            value={form.phone}
            onChange={(e) => updateField("phone", e.target.value)}
            maxLength={20}
            className="h-12"
            placeholder="Best number to reach you"
            autoComplete="tel"
            aria-invalid={!!errors.phone}
            aria-describedby={errors.phone ? "q-phone-error" : errors.contact ? "q-contact-error" : undefined}
          />
          {errors.phone && <p id="q-phone-error" className="text-xs text-destructive">{errors.phone}</p>}
        </div>
      </div>

      {errors.contact && (
        <p id="q-contact-error" className="text-xs text-destructive" role="alert">
          {errors.contact}
        </p>
      )}

      <div className="grid gap-5 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="q-email">
            {isContact ? "Email Address" : <>Email <span className="text-muted-foreground text-xs">(phone or email)</span></>}
          </Label>
          <Input
            id="q-email"
            type="email"
            value={form.email}
            onChange={(e) => updateField("email", e.target.value)}
            maxLength={120}
            className="h-12"
            placeholder={isContact ? "Your email address" : "you@email.com"}
            autoComplete="email"
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? "q-email-error" : errors.contact ? "q-contact-error" : undefined}
          />
          {errors.email && <p id="q-email-error" className="text-xs text-destructive">{errors.email}</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="q-address">
            {isContact ? "Project Address or Town *" : <>Project Address or Town <span className="text-muted-foreground text-xs">(optional)</span></>}
          </Label>
          <Input
            id="q-address"
            value={form.address}
            onChange={(e) => updateField("address", e.target.value)}
            maxLength={160}
            className="h-12"
            placeholder={isContact ? "Street address or town" : "Street address, or just your town"}
            aria-invalid={!!errors.address}
            aria-describedby={errors.address ? "q-address-error" : undefined}
          />
          {errors.address && <p id="q-address-error" className="text-xs text-destructive">{errors.address}</p>}
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="q-service">Service Interested In *</Label>
          <Select value={form.service} onValueChange={(value) => updateField("service", value)}>
            <SelectTrigger id="q-service" className="h-12" aria-invalid={!!errors.service} aria-describedby={errors.service ? "q-service-error" : undefined}>
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent>
              {serviceOptions.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.service && <p id="q-service-error" className="text-xs text-destructive">{errors.service}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="q-timing">
            Desired Project Timing <span className="text-muted-foreground text-xs">(optional)</span>
          </Label>
          <Select value={form.timing} onValueChange={(value) => updateField("timing", value)}>
            <SelectTrigger id="q-timing" className="h-12">
              <SelectValue placeholder="When would you like to start?" />
            </SelectTrigger>
            <SelectContent>
              {TIMING_OPTIONS.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <fieldset className="space-y-3">
        <legend className="text-sm font-medium text-foreground">
          Preferred Contact Method <span className="text-muted-foreground text-xs">(optional)</span>
        </legend>
        <div className="flex flex-wrap gap-3">
          {CONTACT_METHODS.map((method) => {
            const selected = form.contactMethod === method;
            return (
              <button
                key={method}
                type="button"
                aria-pressed={selected}
                onClick={() => updateField("contactMethod", selected ? "" : method)}
                className={`px-6 h-12 border text-[11px] uppercase tracking-[0.16em] transition-colors ${
                  selected
                    ? "border-clay bg-clay text-primary-foreground"
                    : "border-border bg-background text-foreground hover:border-clay/60"
                }`}
              >
                {method}
              </button>
            );
          })}
        </div>
      </fieldset>

      <div className="space-y-2">
        <Label htmlFor="q-details">Project Details</Label>
        <Textarea
          id="q-details"
          value={form.details}
          onChange={(e) => updateField("details", e.target.value)}
          maxLength={1000}
          rows={5}
          placeholder={isContact ? "Tell us about your project, rooms, approximate size, finish ideas, timing or anything else we should know." : "Tell us about your project: rooms, square footage, finish ideas, timing, or anything else we should know."}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="q-photos">
          Project Photos <span className="text-muted-foreground text-xs">(optional)</span>
        </Label>
        <Input
          id="q-photos"
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic"
          multiple
          onChange={handlePhotoChange}
          className="h-12 cursor-pointer file:mr-4 file:text-sm file:text-foreground"
          aria-invalid={!!errors.photos}
          aria-describedby={errors.photos ? "q-photos-help q-photos-error" : "q-photos-help"}
        />
        <p id="q-photos-help" className="text-xs text-muted-foreground">
          Up to {MAX_PHOTOS} photos, 10 MB each. JPG, PNG, WEBP or HEIC.
        </p>
        {errors.photos && <p id="q-photos-error" className="text-xs text-destructive">{errors.photos}</p>}
        {photos.length > 0 && (
          <ul className="flex flex-wrap gap-2 pt-1">
            {photos.map((file, index) => (
              <li
                key={`${file.name}-${index}`}
                className="flex items-center gap-2 border border-border bg-background px-3 py-2 text-xs text-foreground"
              >
                <span className="max-w-[160px] truncate">{file.name}</span>
                <button
                  type="button"
                  onClick={() => removePhoto(index)}
                  aria-label={`Remove ${file.name}`}
                  className="text-muted-foreground hover:text-destructive transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {errors.submit && <p role="alert" className="text-sm text-destructive">{errors.submit}</p>}

      <Button type="submit" size="lg" className="w-full h-14 text-base" disabled={isSubmitting}>
        {isSubmitting ? "Sending…" : isContact ? "Get My Free Estimate →" : "Request Free Quote"}
      </Button>

      <p className="text-xs text-muted-foreground text-center">
        Free estimates. No obligation. Call or text (201) 701-8248.
      </p>
    </form>
  );
};

export default QuoteForm;
